import React, { useEffect, useState } from "react";
import { Html } from "@react-three/drei";
import { useDispatch, useSelector } from "react-redux";
import TextField from "@mui/material/TextField";
import { Typography } from "../../design_system/StyledComponents/components/Typography.js";
import { Button } from "../../design_system/StyledComponents/components/Button.js";
import LengthConverter from "./LengthConverter.js";
import { setMeasured, setUserLength } from "../../features/drawing/drwingSlice.js";
import { setShowSetScalePopup, setFirstLinePoints, setStoreLines, setPoints, setDesignStep } from "../../Actions/ApplicationStateAction.js";
import { useActions } from "../../hooks/useActions.js";
import usePoints from "../../hooks/usePoints.js";
import "./ScaleContextMenu.css";
import Draggable from "react-draggable";

const ScaleContextMenu = () => {
  const dispatch = useDispatch();
  const { userLength, measured } = useSelector((state) => state.drawing);
  const { showSetScalePopup, firstLinePoints, imageUri } = useSelector((state) => state.ApplicationState);
  const { handleDoubleClick } = useActions();
  const { decimalToFeetInches } = usePoints();
  const [error, setError] = useState(false);
  const [lengthFoot, setLengthFoot] = useState(0);
  const [lengthInch, setLengthInch] = useState(0);

  const position = firstLinePoints && firstLinePoints.length === 2 && firstLinePoints[1]
    ? [firstLinePoints[1].x + 10, firstLinePoints[1].y - 10, firstLinePoints[1].z]
    : [0, 0, 0];

  const handleConfirmClick = () => {
    if (imageUri) {
      if (measured === "ft") {
        if ((lengthFoot <= 0 && lengthInch <= 0) || lengthFoot === null || lengthInch === null) {
          setError(true);
          return;
        }
        const length = parseInt(lengthFoot) + (lengthInch / 12);
        const defaultWidth = 1; // Default width in feet
        handleDoubleClick(length, defaultWidth);
      } else {
        if (userLength <= 0 || userLength === undefined || userLength === "" || userLength === null) {
          setError(true);
          return;
        }
        const defaultWidth = 150; // Default width in mm
        handleDoubleClick(userLength, defaultWidth);
      }
    } else {
      // When no image, confirm unit selection and go to designStep 2
      dispatch(setDesignStep(2));
    }
    dispatch(setShowSetScalePopup(false));
  };

  const handleRedrawClick = () => {
    dispatch(setStoreLines([]));
    dispatch(setPoints([]));
    dispatch(setFirstLinePoints([]));
    dispatch(setShowSetScalePopup(false));
  };

  const handleUnitChange = (event) => {
    dispatch(setMeasured(event.target.value));
  };

  useEffect(() => {
    if (userLength > 0 && error) {
      setError(false);
    }
  }, [userLength, error]);

  useEffect(() => {
    if (measured === "ft") {
      const length = decimalToFeetInches(userLength || 0);
      setLengthFoot(length.feet);
      setLengthInch(length.inches);
    }
  }, [userLength, measured]);



  return (
    <Html position={imageUri ? position : [0, 0, 0]} 
    className={`scale-context-menu-component ${!imageUri ? 'no-image' : 'has-image'}`}>
      <div className=".scale-context-menu-container">
        <div className="scale-context-menu-container">
          <div className="scale-input-container">
            {imageUri ? (
              <>
                <Typography modifiers={["subtitle2", "bold"]} style={{ marginBottom: "12px" }}>
                  Enter wall length
                </Typography>
                <div className="unit-and-length">
                  <div className="length-input-group">
                    <Typography modifiers={["subtitle3", "bold"]}>Length</Typography>
                    {measured === "ft" ? (
                      <div className="feet-input-group">
                        <TextField
                          placeholder="ft"
                          label="ft"
                          variant="outlined"
                          size="small"
                          value={lengthFoot !== "" ? lengthFoot : ""}
                          onChange={(e) => setLengthFoot(e.target.value ? parseInt(e.target.value) : 0)}
                          InputProps={{
                            style: {
                              fontSize: "14px",
                              fontFamily: "'DM Sans', sans-serif",
                              height: "36px",
                              width: "60px",
                              borderRadius: "6px",
                            },
                          }}
                        />
                        <TextField
                          placeholder="in"
                          label="in"
                          variant="outlined"
                          size="small"
                          value={lengthInch !== "" ? lengthInch : ""}
                          onChange={(e) => {
                            if (e.target.value.length <= 11)
                              setLengthInch(e.target.value ? parseInt(e.target.value) : 0);
                          }}
                          InputProps={{
                            style: {
                              fontSize: "14px",
                              fontFamily: "'DM Sans', sans-serif",
                              height: "36px",
                              width: "60px",
                              borderRadius: "6px",
                            },
                          }}
                        />
                      </div>
                    ) : (
                      <TextField
                        placeholder={measured}
                        label={measured}
                        variant="outlined"
                        size="small"
                        value={userLength !== "" ? userLength : ""}
                        onChange={(e) => dispatch(setUserLength(e.target.value))}
                        InputProps={{
                          style: {
                            fontSize: "14px",
                            fontFamily: "'DM Sans', sans-serif",
                            height: "36px",
                            width: "120px",
                            borderRadius: "6px",
                          },
                        }}
                      />
                    )}
                  </div>
                  <div id="dropdown">
                    <LengthConverter measured={measured} handleUnitChange={handleUnitChange} labelNeeded={false} />
                  </div>
                </div>
                {error && (
                  <Typography modifiers={["warning300", "helpText"]} style={{ marginTop: "4px" }}>
                    Please enter a valid length
                  </Typography>
                )}
                <div className="scale-button-container">
                  <Button
                    onClick={handleRedrawClick}
                    modifiers={["outline", "small"]}
                    style={{ marginRight: "8px" }}
                  >
                    Redraw
                  </Button>
                  <Button
                    onClick={handleConfirmClick}
                    modifiers={["blue", "small"]}
                  >
                    Confirm
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Typography modifiers={["subtitle2"]} style={{ marginBottom: "12px" }}>
                  Set Unit
                </Typography>
                <Typography modifiers={["subtitle3"]} style={{ marginBottom: "8px" }}>
                  Select length unit
                </Typography>
                <div id="dropdown">
                  <LengthConverter measured={measured} handleUnitChange={handleUnitChange} labelNeeded={false} />
                </div>
                <div className="scale-button-container">
                  <Button
                    onClick={handleConfirmClick}
                    modifiers={["blue", "small"]}
                  >
                    Save and Continue
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Html>
  );
};

export default ScaleContextMenu;
